"use client"

import { useNavigation } from "@/hooks"
import { HandledApiError, UnhandledApiError } from "@/services/api-config"
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { ReactNode, useState } from "react"
import { prettifyError, ZodError } from "zod"

/* -------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/
type QueryProviderProps = {
  children: ReactNode
}

/* -------------------------------------------------------------------------------------------------
 * component
 * -----------------------------------------------------------------------------------------------*/
const QueryProvider = ({ children }: QueryProviderProps) => {
  /*--------------------hooks--------------------*/
  const router = useNavigation()

  /*--------------------helpers--------------------*/
  const onError = (error: unknown) => {
    const defaultErrorPage = "/errore"

    if (error instanceof UnhandledApiError) {
      console.error(`UNHANDLED-API-ERROR ${error}`)
      return router.push(error.errorRedirect)
    }

    if (error instanceof ZodError) {
      console.error(`ZOD-ERROR ${prettifyError(error)}`)
      return router.push(defaultErrorPage)
    }

    //se è un HandledApiError abbiamo fatto noi il throw e lo gestiamo nell' error property della query / mutation
    //altrimeni sarà un Error normale
    if (!(error instanceof HandledApiError)) {
      console.error(`ERROR ${error}`)
      router.push(defaultErrorPage)
    }
  }

  /*--------------------config--------------------*/
  const [queryClient] = useState(
    new QueryClient({
      queryCache: new QueryCache({
        onError: (error, query) => {
          const meta = query.meta ?? {}
          if ("preventGlobalError" in meta && meta.preventGlobalError) return
          onError(error)
        },
      }),
      mutationCache: new MutationCache({
        onError: (error, variables, context, mutation) => {
          const meta = mutation.meta ?? {}
          if ("preventGlobalError" in meta && meta.preventGlobalError) return
          onError(error)
        },
      }),
      defaultOptions: {
        queries: {
          retry: false,
          refetchOnWindowFocus: false,
          queryFn: async () => null,
        },
      },
    }),
  )

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}

export { QueryProvider }
